import { useState, useEffect, useRef, useMemo } from "react";
import { DateTime } from "luxon";
import {
    useClientPortalSelector,
    selectBooking,
    setAvailability,
    useClientPortalDispatch,
    selectBookingType,
    setProvider,
    setGroup,
} from "../../../../redux";
import { useBookingNavigation } from "../../hooks/useBookingNavigation";
import {
    getAvailability,
    ISlot,
    slotsForSelectedDay,
} from "../../../Shared/AvailabilityShared/AvailabilityShared.helpers";

import { useAvailability } from "hooks/useAvailability";

import AvailabilityShared from "../../../Shared/AvailabilityShared";
import { AvailabilityTimeModel, IdNameItemModel } from "types/generated/models";
import { useAnalytics } from "hooks/useAnalytics";
import { IGroupData, IProviderData, IAvailabilityData } from "types/appointmentBooking";
import { useClientPortalConfigContext } from "context/ClientPortalConfig.ctx";

export default function Availability() {
    const dispatch = useClientPortalDispatch();
    const { location, service, provider, availability, session, availableProviders } =
        useClientPortalSelector(selectBooking);
    const bookingType = useClientPortalSelector(selectBookingType);
    const { track } = useAnalytics();
    const { handleNext } = useBookingNavigation();
    const {
        clientPortalConfig: { defaultStripeAccount },
    } = useClientPortalConfigContext();

    const [currentMonth, setCurrentMonth] = useState(() => {
        if (availability?.data?.timeEntry?.dateTimeOffset) {
            const reduxSlotDate = DateTime.fromISO(availability.data.timeEntry.dateTimeOffset);
            return DateTime.fromObject({
                year: reduxSlotDate.year,
                month: reduxSlotDate.month,
                day: 1,
            }) as DateTime;
        }
        return DateTime.now();
    });

    const [selectedDate, setSelectedDate] = useState<DateTime | null>(() => {
        if (availability?.data?.timeEntry?.dateTimeOffset) {
            const reduxSlotDate = DateTime.fromISO(availability.data.timeEntry.dateTimeOffset);
            return reduxSlotDate.startOf("day");
        }
        return null;
    });

    const [selectedSlot, setSelectedSlot] = useState<ISlot | null>(null);
    const hasInitializedFromRedux = useRef(false);

    const {
        availability: availabilityData,
        isLoading,
        refetch,
    } = useAvailability({
        locationId: location?.data?.locationId || 0,
        serviceId: service?.data?.serviceId || 0,
        providerId: provider?.data?.providerId || 0,
        year: currentMonth.year,
        month: currentMonth.month,
        availabilityType: 1,
        enableTimezones: true,
        clientUtcOffset: DateTime.now().offset,
    });

    const weeks = useMemo(() => {
        return availabilityData ? getAvailability(currentMonth, availabilityData) : [];
    }, [availabilityData, currentMonth]);

    useEffect(() => {
        if (availability?.data?.timeEntry?.dateTimeOffset && weeks.length > 0 && !hasInitializedFromRedux.current) {
            const reduxSlotDate = DateTime.fromISO(availability.data.timeEntry.dateTimeOffset);
            const targetDateTimeOffset = availability.data.timeEntry.dateTimeOffset;

            const findMatchingSlot = (reduxSlotDate: DateTime, targetDateTimeOffset: string): ISlot | null => {
                for (const week of weeks) {
                    for (const day of week) {
                        if (!day.date?.startOf("day").equals(reduxSlotDate.startOf("day"))) continue;

                        const slots = slotsForSelectedDay(day);
                        const allSlots: ISlot[] = [
                            ...(slots.morningSlots || []),
                            ...(slots.afternoonSlots || []),
                            ...(slots.eveningSlots || []),
                        ];

                        const foundSlot = allSlots.find((slot) => slot.dateTimeOffset === targetDateTimeOffset);
                        if (foundSlot) return foundSlot;
                    }
                }
                return null;
            };

            const foundSlot = findMatchingSlot(reduxSlotDate, targetDateTimeOffset);
            if (foundSlot) {
                setSelectedSlot(foundSlot);
            }

            hasInitializedFromRedux.current = true;
        }
    }, [availability?.data, weeks]);

    useEffect(() => {
        const locationId = location?.data?.locationId;
        const serviceId = service?.data?.serviceId;
        const providerId = provider?.data?.providerId;

        if (locationId && serviceId && providerId !== undefined) {
            refetch();
        }
    }, [currentMonth, location?.data, service?.data, provider?.data, refetch]);

    const handleSlotSelection = (slot: ISlot, selectedProvider?: IdNameItemModel) => {
        setSelectedSlot(slot);

        const timeEntry: AvailabilityTimeModel = {
            ...slot.originalTimeEntry,
            providerId: selectedProvider ? selectedProvider.id : slot.providerId,
        };

        const slotDate = DateTime.fromISO(slot.dateTimeOffset);
        const displayText = `${slotDate.toFormat("MMM dd, yyyy")} at ${slotDate.toFormat("h:mm a")}`;
        const isoDate = slotDate.toISODate();

        if (isoDate) {
            const availabilityData: IAvailabilityData = {
                timeEntry,
                selectedProvider,
                date: isoDate,
                displayText,
            };

            dispatch(setAvailability(availabilityData));
            track("booking_step_availability_completed", {
                booking_type: session.bookingType,
                selected_date: isoDate,
                selected_time: slotDate.toFormat("h:mm a"),
                provider_id: selectedProvider ? selectedProvider.id : slot.providerId,
                provider_name: selectedProvider ? selectedProvider.name : undefined,
            });
            handleNext();
        }
    };

    const handleProviderSelect = (providerData: IProviderData) => {
        dispatch(setProvider(providerData));
    };

    const handleGroupSelect = (groupData: IGroupData) => {
        dispatch(setGroup(groupData));
    };

    const providerData: IProviderData = {
        providerId: provider?.data?.providerId ?? 0,
        providerName: provider?.data?.providerName ?? "",
        providerStripeAccount: provider?.data?.providerStripeAccount,
    };

    return (
        <AvailabilityShared
            weeks={weeks}
            availabilityData={availabilityData}
            selectedDate={selectedDate}
            setSelectedSlot={setSelectedSlot}
            selectedSlot={selectedSlot}
            isLoading={isLoading}
            currentMonth={currentMonth}
            setCurrentMonth={setCurrentMonth}
            setSelectedDate={setSelectedDate}
            handleSlotSelection={handleSlotSelection}
            provider={providerData}
            availableProviders={availableProviders}
            bookingType={bookingType}
            defaultStripeAccount={defaultStripeAccount}
            onProviderSelect={handleProviderSelect}
            onGroupSelect={handleGroupSelect}
        />
    );
}
